import { useState, useEffect, useCallback } from 'react';
import { awsAuthService, AWSAuthCredentials, AWSEnvironment } from '../utils/awsAuth';

export interface UseAWSAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  environmentInfo: {
    baseUrl: string;
    environment: AWSEnvironment;
  };
  login: (credentials: AWSAuthCredentials) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

export const useAWSAuth = (): UseAWSAuthReturn => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Check authentication status on mount and periodically
  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(awsAuthService.isAuthenticated());
    };
    
    // Check immediately
    checkAuth();
    
    // Check periodically to catch authentication changes
    const interval = setInterval(checkAuth, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const login = useCallback(async (credentials: AWSAuthCredentials): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await awsAuthService.login(credentials);
      setIsAuthenticated(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'AWS authentication failed';
      setError(errorMessage);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback((): void => {
    awsAuthService.logout();
    setIsAuthenticated(false);
    setError(null);
  }, []);

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  const environmentInfo = awsAuthService.getEnvironmentInfo();

  return {
    isAuthenticated,
    isLoading,
    error,
    environmentInfo,
    login,
    logout,
    clearError,
  };
};
